import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  booleanAttribute,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Select } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { Pagination } from '@app/shared/model/pagination.model';
import { ApiService } from '@core/providers/http';
import {
  BolsaService,
  BolseiroService,
  InstituicaoEnsinoService,
  GenericaService,
} from '../../../api/services';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import { Subject, takeUntil } from 'rxjs';
import { toExtensoComMoeda } from '@app/core/helpers/toExtensoComMoeda';

interface VerificarRegraPagamentoPayload {
  id: string;
  pedidoID: number;
  bolseiroID: number;
  requerenteID: number;
  isActive: boolean;
  tipoBolsa: number;
  paisID: number;
  instituicaoGestoraID: number;
  nivelAcademico: number;
  areaProgramaFormacaoID: number;
  programaFormacaoID: number;
  anoCurricularFrequencia: string;
  valorSubsidio: number;
}

@Component({
  selector: 'app-memorando-pagamento-de-subsidios-externos',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    Select,
    InputTextModule,
    DataVoidComponent,
  ],
  providers: [
    ApiService,
    BolseiroService,
    BolsaService,
    InstituicaoEnsinoService,
    GenericaService,
  ],
  templateUrl: './memorando-pagamento-de-subsidios-externos.component.html',
  styleUrl: './memorando-pagamento-de-subsidios-externos.component.scss',
})
export class MemorandoPagamentoDeSubsidiosExternosComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input() bolseiroID!: string;
  @Input() documento!: any;

  @Output() formValueChanges = new EventEmitter<{
    data: any;
    valid: boolean;
  }>();

  private destroy$ = new Subject<void>();

  // services
  fb = inject(FormBuilder);
  bolsaService = inject(BolsaService);
  bolseiroService = inject(BolseiroService);
  instituicaoEnsinoService = inject(InstituicaoEnsinoService);
  genericaService = inject(GenericaService);

  // state
  form!: FormGroup;
  pagination: Pagination<any> = new Pagination<any>();
  estudantes: Array<{ id: number; nome: string }> = [];
  paisGenericoList: any[] = [];
  paisID!: number;

  bolsa: any;
  valorRegra: string | null = null; // formatado pt-BR (ex.: "1.250,00")

  // --- lifecycle ---
  ngOnInit(): void {
    this.buildForm();
    this.wireCoreChanges();
    this.getPaisGenerico();
    // this.syncDisabled();
  }

  ngOnChanges(): void {
    // this.syncDisabled();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- form ---
  private buildForm(): void {
    this.form = this.fb.group({
      paisBolsa: ['', [Validators.required]],
      estudanteID: ['', [Validators.required]],
      bolsaID: ['', [Validators.required]],
      valores: [{ value: '', disabled: true }, [Validators.required]],
      valoresextenso: [{ value: '', disabled: true }, [Validators.required]],
      moeda: [''],
      idioma: ['pt', [Validators.required]],
    });
  }

  private syncDisabled(): void {
    if (!this.form) return;
    this.disabled
      ? this.form.disable({ emitEvent: false })
      : this.form.enable({ emitEvent: false });
  }

  // --- subscriptions ---
  private wireCoreChanges(): void {
    // Emite payload em qualquer mudança relevante
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emitPayload());
    this.form.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emitPayload());

    // Valor → gera extenso (parse PT → número)
    this.form
      .get('valores')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((valor) => {
        const num = this.parsePtNumber(valor);
        if (num !== null) {
          this.form
            .get('valoresextenso')
            ?.setValue(
              toExtensoComMoeda(num, this.form.get('moeda')?.value || ''),
              { emitEvent: true }
            );
        } else {
          this.form.get('valoresextenso')?.setValue('', { emitEvent: true });
        }
      });

    // Estudante mudou → reset e recarregar bolsa/regra
    this.form
      .get('estudanteID')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.resetBolsaCampos(); // <<<<<< regra de negócio aplicada
        if (!value) return;
        this.loadAllBolsa({ BolseiroID: value });
      });
  }

  // --- business helpers ---
  private resetBolsaCampos(): void {
    this.bolsa = null;
    this.valorRegra = null;
    this.form.patchValue(
      {
        bolsaID: '',
        valores: '',
        valoresextenso: '',
        moeda: '',
      },
      { emitEvent: true }
    );
    this.pagination = new Pagination<any>().deserialize([]);
  }

  private pickMatricula(b: any) {
    const arr = Array.isArray(b?.matricula) ? b.matricula : [];
    if (!arr.length) return null;

    const emCurso = arr.find(
      (m: any) => (m?.estadoDescricao || '').toLowerCase() === 'em curso'
    );
    if (emCurso) return emCurso;

    const parse = (d: any) => (d ? new Date(d).getTime() : 0);
    return (
      arr
        .slice()
        .sort(
          (a: any, c: any) => parse(c?.dataInicio) - parse(a?.dataInicio)
        )[0] || null
    );
  }

  private buildRegraPayload(bolsa: any): VerificarRegraPagamentoPayload {
    const matricula = this.pickMatricula(bolsa);
    const anoCurricular = matricula?.anoCurricular ?? bolsa?.anoCurricular ?? 1;

    const programaFormacaoID =
      bolsa?.programaFormacaoID ??
      bolsa?.areaProgramaFormacao?.programaFormacaoId ??
      bolsa?.areaProgramaFormacao?.programaFormacaoID ??
      0;

    const bolseiroID = Number(bolsa?.bolseiroID ?? bolsa?.bolseiro?.id ?? 0);

    return {
      id: '0',
      pedidoID: Number(bolsa?.pedidoID ?? 0),
      bolseiroID,
      requerenteID: Number(bolsa?.requerenteID ?? 0),
      isActive: Boolean(bolsa?.isActive ?? true),
      tipoBolsa: Number(bolsa?.tipoBolsa ?? 0),
      paisID: Number(bolsa?.paisID ?? 0),
      instituicaoGestoraID: Number(
        bolsa?.instituicaoGestoraID ?? bolsa?.instituicaoGestora?.id ?? 0
      ),
      nivelAcademico: Number(bolsa?.nivelAcademico ?? 0),
      areaProgramaFormacaoID: Number(
        bolsa?.areaProgramaFormacaoID ?? bolsa?.areaProgramaFormacao?.id ?? 0
      ),
      programaFormacaoID: Number(programaFormacaoID),
      anoCurricularFrequencia: String(anoCurricular),
      valorSubsidio: Number(bolsa?.valorSubsidio ?? 0),
    };
  }

  private fetchValorByRegra(bolsa: any): void {
    if (!bolsa) {
      this.valorRegra = null;
      this.emitPayload();
      return;
    }

    const body = this.buildRegraPayload(bolsa);
    this.bolsaService
      .verificarRegraPagamento(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          let valor = Number(res?.valorPagamento ?? 0);
          let moeda = this.form.get('moeda')?.value ?? '';

          if (!valor || Number.isNaN(valor)) {
            const lista = Array.isArray(res?.regraPagamentoSubsidio)
              ? res.regraPagamentoSubsidio
              : [];
            const apoioFormacao =
              lista.find((x: any) => x?.subsidioId === 2) ??
              lista.find((x: any) =>
                String(x?.subsidios?.descricao ?? '')
                  .toLowerCase()
                  .includes('apoio a formação')
              );
            valor = Number(apoioFormacao?.valor ?? 0);
            moeda = apoioFormacao?.moeda ?? '';
          }

          // guarda formatações
          this.valorRegra = valor > 0 ? this.formatPtBR(valor) : null;

          // preenche inputs “valores”, “moeda”, “valoresextenso”
          const ext = valor > 0 ? toExtensoComMoeda(valor, moeda) : '';
          this.form.patchValue(
            {
              valores: this.valorRegra ?? '',
              valoresextenso: ext,
              moeda,
            },
            { emitEvent: true }
          );
        },
        error: () => {
          this.valorRegra = null;
          this.form.patchValue(
            { valores: '0,00', valoresextenso: 'zero', moeda: '-' },
            { emitEvent: true }
          );
        },
      });
  }

  // --- data ---
  onPaisChange(): void {
    this.paisID = this.form.get('paisBolsa')?.value;
    if (!this.paisID) return;

    this.getEstudantesPorPais(this.paisID);
    this.form.patchValue(
      { estudanteID: null, bolsaID: null },
      { emitEvent: true }
    );
    this.pagination = new Pagination<any>().deserialize([]);
  }

  getPaisGenerico(): void {
    this.genericaService
      .listPaisGenerico({ tipoBolsa: 2 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => (this.paisGenericoList = resp?.data || resp || []),
        error: (e) => console.error('Erro ao carregar países genéricos:', e),
      });
  }

  getEstudantesPorPais(paisID: number): void {
    this.bolsaService
      .list({ pageSize: 100, OrderBy: 'id desc', PaisID: paisID })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const data = response?.data || [];
          this.estudantes = data.map((bolsa: any) => ({
            id: bolsa?.bolseiro?.id ?? bolsa?.id,
            nome: bolsa?.bolseiro?.nome ?? 'Estudante sem nome',
          }));
        },
        error: (e) => console.error('Erro ao carregar estudantes por país:', e),
      });
  }

  loadAllBolsa(queryParams: any = {}): void {
    const params = {
      ...queryParams,
      pageNumber: this.pagination.pageNumber ?? 1,
      pageSize: this.pagination.pageSize ?? 5,
    };

    this.bolsaService
      .list({
        ...params,
        BolseiroID: queryParams.BolseiroID || this.bolseiroID,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.pagination = new Pagination<any>().deserialize(response);
          this.bolsa = response?.data?.[0] ?? null;
          this.form
            .get('bolsaID')
            ?.setValue(this.bolsa?.id ?? '', { emitEvent: true });
          this.fetchValorByRegra(this.bolsa);
        },
        error: (e) => console.error('Erro ao carregar bolsa:', e),
      });
  }

  // --- UI handlers ---
  onRadioChange(event: any): void {
    this.form.get('bolsaID')?.setValue(event?.id ?? null, { emitEvent: true });
  }

  // --- utils ---
  private parsePtNumber(value: unknown): number | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    // mantém dígitos, vírgula, ponto e sinal
    let s = String(value).replace(/[^\d.,-]/g, '');
    // remove separador de milhar (.) quando seguido de 3 dígitos
    s = s.replace(/\.(?=\d{3}(\D|$))/g, '');
    // vírgula decimal → ponto
    s = s.replace(',', '.');

    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  private formatPtBR(n: number): string {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n || 0);
  }

  private emitPayload(): void {
    const data = {
      bolsaID: this.form.get('bolsaID')?.value,
      valores: String(this.form.get('valores')?.value ?? ''),
      valoresextenso: this.form.get('valoresextenso')?.value,
      moeda: this.form.get('moeda')?.value,
      idioma: this.form.get('idioma')?.value,
      tipoDocumento: 3,
      paisBolsa: this.form.get('paisBolsa')?.value,
      estudanteID: this.form.get('estudanteID')?.value,
    };
    this.formValueChanges.emit({ data, valid: this.form.valid });
  }
}
